import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GearSix,
  SignOut,
  User,
  Lightning,
  Storefront,
} from '@phosphor-icons/react';
import { GiTreasureMap } from 'react-icons/gi';
import { useAuthStore } from '@/stores/authStore';
import { useVibeCoinStore } from '@/stores/vibecoinStore';
import { useEnergyStore } from '@/stores/energyStore';
import { LanguageSwitcher } from './LanguageSwitcher';
import './PerfilMenu.css';

interface PerfilMenuProps {
  onClose: () => void;
}

export function PerfilMenu({ onClose }: PerfilMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { balance, fetchBalance } = useVibeCoinStore();
  const { energy, maxEnergy, unlimited, energyColor, fetchStatus } = useEnergyStore();

  useEffect(() => { fetchBalance(); fetchStatus(); }, []);

  const getInitials = () => {
    if (!user?.name) return '?';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return user.name[0].toUpperCase();
  };

  const userPlan = 'FREE';

  const handleNav = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Backdrop */}
      <div className="perfil-menu-backdrop" onClick={onClose} />

      {/* Menu */}
      <div className="perfil-menu">
        {/* User Header */}
        <div className="perfil-menu-header">
          <div className="perfil-menu-avatar">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt={user.name} className="perfil-menu-avatar-img" />
            ) : (
              <span>{getInitials()}</span>
            )}
          </div>
          <div className="perfil-menu-info">
            <span className="perfil-menu-name">{user?.name || t('user.defaultName')}</span>
            <span className="perfil-menu-email">{user?.email}</span>
          </div>
        </div>

        {/* Plan Status */}
        <div className="perfil-menu-plan">
          <span className="perfil-plan-label">{t('menu.planoAtual')}:</span>
          <span className={`perfil-plan-value plan-${userPlan.toLowerCase()}`}>{userPlan}</span>
        </div>

        {/* Upgrade CTA */}
        <button className="perfil-menu-upgrade" onClick={() => handleNav('/dashboard?view=pricing')}>
          <Lightning weight="fill" size={14} />
          <span>{t('menu.fazerUpgrade')}</span>
        </button>

        <div className="perfil-menu-divider" />

        {/* Stats */}
        {user && (
          <div className="perfil-menu-stats">
            <div className="perfil-stat-row">
              <span className="perfil-stat-label">{t('stats.odysseyLevel')}</span>
              <span className="perfil-stat-value">{user.odyssey_level || user.level || 1}</span>
            </div>
            <div className="perfil-stat-row" style={{ cursor: 'pointer' }} onClick={() => handleNav('/store')}>
              <span className="perfil-stat-label">
                <img src="/image/icons/vibecoin-16.png" alt="" width={12} height={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {t('stats.vibeCoins')}
              </span>
              <span className="perfil-stat-value coins">{balance.toLocaleString()}</span>
            </div>
            <div className="perfil-stat-row">
              <span className="perfil-stat-label">
                <Lightning weight="fill" size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: energyColor() }} />
                {t('stats.energy')}
              </span>
              <span className="perfil-stat-value" style={{ color: energyColor() }}>
                {unlimited ? '∞' : `${energy}/${maxEnergy}`}
              </span>
            </div>
          </div>
        )}

        <div className="perfil-menu-divider" />

        {/* Menu Items */}
        <button className="perfil-menu-item" onClick={() => handleNav('/minha-ficha')}>
          <User weight="regular" size={15} />
          <span>{t('menu.minhaFicha')}</span>
        </button>

        <button className="perfil-menu-item" onClick={() => handleNav('/settings')}>
          <GearSix weight="regular" size={15} />
          <span>{t('menu.configuracoes')}</span>
        </button>

        <button className="perfil-menu-item" onClick={() => handleNav('/store')}>
          <Storefront weight="regular" size={15} />
          <span>{t('menu.loja')}</span>
        </button>

        <button className="perfil-menu-item" onClick={() => handleNav('/convites')}>
          <GiTreasureMap size={15} />
          <span>{t('menu.convites')}</span>
        </button>

        {/* Language Switcher */}
        <LanguageSwitcher />

        <div className="perfil-menu-divider" />

        {/* Logout */}
        <button className="perfil-menu-item logout" onClick={handleLogout}>
          <SignOut weight="regular" size={15} />
          <span>{t('menu.sair')}</span>
        </button>
      </div>
    </>
  );
}
